import { useState, useMemo, useCallback, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import { Button, Input, Select, Space, Table, Tag, Tooltip, Popconfirm, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useDelete, getDefaultFilter, useGo } from "@refinedev/core";

const { Text } = Typography;

type AIStyle = {
  id: string;
  style: string;
  system_prompt: string;
  llm_model: string;
  temperature: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export default function AIStylesList() {
  const go = useGo();
  const {
    tableProps,
    filters,
    setFilters,
    tableQuery,
  } = useTable<AIStyle>({
    resource: "settings/ai-styles",
    pagination: { mode: "server", pageSize: 20 },
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
    filters: {
      initial: [
        { field: "style", operator: "contains", value: undefined },
        { field: "is_active", operator: "eq", value: undefined },
      ],
    },
    syncWithLocation: true,
  });

  const { mutate: deleteAIStyle } = useDelete();

  const [searchDraft, setSearchDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState<boolean | undefined>();

  const appliedSearchValue = useMemo(() => {
    const f = getDefaultFilter("style", filters);
    return typeof f?.value === "string" ? f.value : "";
  }, [filters]);

  const appliedStatusValue = useMemo(() => {
    const f = getDefaultFilter("is_active", filters);
    return (f?.value as boolean | undefined) ?? undefined;
  }, [filters]);

  useEffect(() => {
    setSearchDraft(appliedSearchValue);
  }, [appliedSearchValue]);

  useEffect(() => {
    setStatusDraft(appliedStatusValue);
  }, [appliedStatusValue]);

  const updateFilters = useCallback(
    (search: string, status?: boolean) => {
      const trimmed = search.trim();
      setFilters(
        [
          { field: "style", operator: "contains", value: trimmed || undefined },
          { field: "is_active", operator: "eq", value: status !== undefined ? status : undefined },
        ],
        "replace"
      );
    },
    [setFilters]
  );

  const handleDelete = useCallback(
    (id: string, style: string) => {
      deleteAIStyle(
        {
          resource: "settings/ai-styles",
          id,
          successNotification: () => ({
            message: `AI стиль "${style}" удалён`,
            description: "",
            type: "success",
          }),
        },
        {
          onSuccess: () => {
            tableQuery.refetch();
          },
        }
      );
    },
    [deleteAIStyle, tableQuery]
  );

  const renderModel = (model: string) => {
    const modelMap: Record<string, { label: string; color: string }> = {
      "deepseek-chat": { label: "DeepSeek Chat", color: "blue" },
    };

    const config = modelMap[model] || { label: model, color: "default" };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const columns: ColumnsType<AIStyle> = [
    {
      title: "Дата создания",
      dataIndex: "created_at",
      width: 120,
      sorter: true,
      defaultSortOrder: "descend",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      align: "center"
    },
    {
      title: "Название стиля",
      dataIndex: "style",
      width: 180,
      filteredValue: appliedSearchValue ? [appliedSearchValue] : null,
      filterIcon: <SearchOutlined style={{ color: appliedSearchValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 260 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            allowClear
            placeholder="Поиск по названию"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onPressEnter={() => {
              updateFilters(searchDraft, statusDraft);
              close();
            }}
            style={{ marginBottom: 8 }}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setSearchDraft("");
              updateFilters("", statusDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (style, record) => (
        <Space>
          <Text strong={record.is_active} style={{ color: record.is_active ? undefined : "#999" }}>
            {style}
          </Text>
          {(record as any).is_default && (
            <Tag color="gold" style={{ fontSize: 11 }}>По умолчанию</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "System Prompt",
      dataIndex: "system_prompt",
      ellipsis: {
        showTitle: false,
      },
      render: (prompt) => (
        <Tooltip placement="topLeft" title={prompt}>
          <Text ellipsis>{prompt}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Модель LLM",
      dataIndex: "llm_model",
      width: 180,
      render: renderModel,
    },
    {
      title: "Температура",
      dataIndex: "temperature",
      width: 120,
      align: "center",
      sorter: true,
      render: (temp) => <Tag color="orange">{temp}</Tag>,
    },
    {
      title: "Статус",
      dataIndex: "is_active",
      width: 110,
      align: "center",
      filteredValue: appliedStatusValue !== undefined ? [appliedStatusValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedStatusValue !== undefined ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 180 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Статус"
            style={{ width: '100%', marginBottom: 8 }}
            value={statusDraft}
            onChange={(value) => setStatusDraft(value)}
            options={[
              { value: true, label: "Активно" },
              { value: false, label: "Неактивно" },
            ]}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setStatusDraft(undefined);
              updateFilters(searchDraft, undefined);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (active: boolean) =>
        active ? (
          <Tag color="success">Активно</Tag>
        ) : (
          <Tag color="default">Неактивно</Tag>
        ),
    },
    {
      title: "Действия",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                go({
                  to: { resource: "settings/ai-styles", action: "edit", id: record.id },
                  type: "push",
                });
              }}
              aria-label="Редактировать"
            />
          </Tooltip>
          <Popconfirm
            title="Удалить AI стиль"
            description={`Вы уверены, что хотите удалить стиль "${record.style}"?`}
            onConfirm={() => handleDelete(record.id, record.style)}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Удалить">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label="Удалить"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title="AI Стили"
        headerButtons={() => (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              go({
                to: {
                  resource: "settings/ai-styles",
                  action: "create",
                },
                type: "push",
              })
            }
          >
            Добавить стиль
          </Button>
        )}
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          bordered
          size="middle"
          scroll={{ x: 1400 }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} AI стилей`,
          }}
        />
      </List>
    </>
  );
}
